import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { preferencesAPI } from '../lib/api';
import {
  DEFAULT_MODE,
  applyTheme,
  isValidMode,
  readStoredMode,
  reconcileMode,
  resolveTheme,
  systemPrefersDark,
  writeStoredMode,
} from '../lib/themeCore';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const stored = useRef(readStoredMode());
  const [mode, setModeState] = useState(() => stored.current?.mode ?? DEFAULT_MODE);
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);
  const hydrated = useRef(false);

  const resolved = resolveTheme(mode, prefersDark);

  // Follow the OS live while in system mode.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setPrefersDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Paint the resolved theme onto <html>.
  useEffect(() => {
    applyTheme(resolved, { animate: hydrated.current });
    hydrated.current = true;
  }, [resolved]);

  const setMode = useCallback((next) => {
    if (!isValidMode(next)) return;
    const updatedAt = Date.now();
    stored.current = { mode: next, updatedAt };
    writeStoredMode(next, updatedAt);
    setModeState(next);
    // Follows the user across devices; a failure here never blocks the UI.
    preferencesAPI.update({ theme_preference: next }).catch(() => {});
  }, []);

  /** Called once the user is known — merges the server value with this device. */
  const hydrateFromServer = useCallback(async () => {
    try {
      const { data } = await preferencesAPI.get();
      const server = { mode: data?.theme_preference, updatedAt: data?.theme_preference_updated_at_ms };
      const winner = reconcileMode(stored.current, server);
      if (winner.mode !== stored.current?.mode) {
        stored.current = { mode: winner.mode, updatedAt: Date.now() };
        writeStoredMode(winner.mode, stored.current.updatedAt);
        setModeState(winner.mode);
      }
      // Push the device value up when it is the newer of the two.
      if (winner.source === 'local' && winner.mode !== server.mode) {
        preferencesAPI.update({ theme_preference: winner.mode }).catch(() => {});
      }
    } catch {/* not signed in yet, or the endpoint is unreachable */}
  }, []);

  const value = useMemo(
    () => ({ mode, resolved, isDark: resolved === 'dark', setMode, hydrateFromServer }),
    [mode, resolved, setMode, hydrateFromServer]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside a ThemeProvider');
  return ctx;
}
